import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { salesApi } from '../salesApi';
import { useT } from '../salesI18n';

// Autocomplete de endereço via backend (Google Places). onSelect recebe {formatted, address, city, state, zip, lat, lng}
export const AddressAutocomplete = ({ value, onChange, onSelect, testId = 'lead-address-input' }) => {
  const { t } = useT();
  const [preds, setPreds] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const skipNext = useRef(false);

  useEffect(() => {
    if (skipNext.current) { skipNext.current = false; return undefined; }
    if (!value || value.trim().length < 3) { setPreds([]); return undefined; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await salesApi.get('/geo/autocomplete', { params: { q: value } });
        setPreds(data.predictions || []);
        setOpen((data.predictions || []).length > 0);
      } catch (e) { setPreds([]); }
    }, 350);
    return () => clearTimeout(timer.current);
  }, [value]);

  const pick = async (p) => {
    setOpen(false);
    skipNext.current = true;
    onChange(p.description);
    setBusy(true);
    try {
      const { data } = await salesApi.get('/geo/geocode', { params: { q: p.description } });
      const parts = (data.formatted || p.description).split(',').map((s) => s.trim());
      const stZip = (parts[2] || '').split(' ');
      onSelect({
        formatted: data.formatted || p.description,
        address: parts[0] || p.description,
        city: parts[1] || '',
        state: stZip[0] || 'GA',
        zip: stZip[1] || '',
        lat: data.lat ?? null,
        lng: data.lng ?? null,
      });
    } catch (e) {
      onSelect({ formatted: p.description, address: p.description, city: '', state: 'GA', zip: '', lat: null, lng: null });
    } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          data-testid={testId}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => preds.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder={t.addressSearch}
          className="pl-9 h-11"
          autoComplete="off"
        />
        {busy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {open && preds.length > 0 && (
        <div data-testid="address-predictions" className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg overflow-hidden">
          {preds.map((p) => (
            <button
              key={p.place_id}
              type="button"
              data-testid="address-prediction-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(p)}
              className="w-full text-left px-3 py-2.5 text-sm hover:bg-accent flex items-start gap-2 transition-colors"
            >
              <MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" />
              <span>{p.description}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-muted-foreground mt-1">{t.addressHint}</p>
    </div>
  );
};
